import React, { useEffect, useState } from "react";
import { X, Check, Zap } from "lucide-react";

const WHATSAPP = process.env.REACT_APP_WHATSAPP_NUMBER || "";

/** Ícone oficial do WhatsApp (logo de marca). */
function WhatsAppIcon({ className = "w-4 h-4" }) {
    return (
        <svg
            viewBox="0 0 32 32"
            fill="currentColor"
            aria-hidden="true"
            className={className}
        >
            <path d="M16.003 0C7.174 0 0 7.174 0 16c0 2.821.732 5.571 2.118 7.984L0 32l8.214-2.142A15.986 15.986 0 0 0 16.003 32C24.828 32 32 24.826 32 16c0-4.266-1.664-8.274-4.686-11.294A15.869 15.869 0 0 0 16.003 0zm-.002 2.667c3.526 0 6.842 1.374 9.332 3.864a13.19 13.19 0 0 1 3.866 9.469c0 7.293-5.936 13.232-13.23 13.232a13.19 13.19 0 0 1-6.72-1.838l-.481-.285-4.878 1.272 1.301-4.754-.314-.49a13.123 13.123 0 0 1-1.917-6.856c.002-7.301 5.94-13.24 13.241-13.24zm-4.73 7.242c-.254 0-.666.096-1.015.477-.349.381-1.334 1.304-1.334 3.178 0 1.873 1.366 3.685 1.556 3.94.19.254 2.63 4.194 6.475 5.712 3.194 1.26 3.843 1.01 4.535.946.692-.063 2.234-.913 2.549-1.795.316-.88.316-1.636.222-1.795-.094-.159-.348-.254-.728-.445-.38-.19-2.234-1.105-2.581-1.232-.348-.127-.6-.19-.854.19-.254.381-.978 1.232-1.2 1.486-.222.254-.443.286-.821.096-.381-.19-1.607-.594-3.06-1.893-1.132-1.01-1.895-2.258-2.116-2.64-.222-.382-.024-.588.167-.778.171-.171.381-.445.572-.668.19-.222.254-.381.381-.635.127-.254.064-.476-.031-.667-.095-.19-.854-2.065-1.17-2.825-.307-.74-.62-.641-.853-.653-.222-.01-.474-.014-.726-.014z" />
        </svg>
    );
}

const PLANS = [
    {
        id: "free",
        name: "Free",
        tokens: "500",
        tasks: "10",
        price: "R$ 0",
        priceNote: "aquisição",
        highlight: false,
    },
    {
        id: "light",
        name: "Light",
        tokens: "1.500",
        tasks: "30",
        price: "R$ 29,90",
        priceNote: "/mês",
        highlight: false,
    },
    {
        id: "plus",
        name: "Plus",
        tokens: "5.000",
        tasks: "100",
        price: "R$ 79,90",
        priceNote: "/mês",
        highlight: true, // destaque
    },
    {
        id: "pro",
        name: "Pro",
        tokens: "15.000",
        tasks: "300",
        price: "R$ 179,90",
        priceNote: "/mês",
        highlight: false,
    },
];

function buildWhatsappUrl(plan) {
    const msg = `Olá! Quero contratar o plano *${plan.name}* do Linkia (${plan.tokens} tokens/mês, ${plan.tasks} tasks/mês — ${plan.price}${plan.priceNote === "/mês" ? "/mês" : ""}).`;
    return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;
}

export default function PlansDialog({ open, onClose }) {
    // Animação suave de entrada/saída
    const [render, setRender] = useState(open);
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (open) {
            setRender(true);
            const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShow(true)));
            return () => cancelAnimationFrame(raf);
        }
        setShow(false);
        const t = setTimeout(() => setRender(false), 250);
        return () => clearTimeout(t);
    }, [open]);

    if (!render) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6"
            data-testid="plans-dialog"
            role="dialog"
            aria-modal="true"
        >
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/80 backdrop-blur-sm ${show ? "opacity-100" : "opacity-0"}`}
                style={{ transitionProperty: "opacity", transitionDuration: "250ms" }}
                onClick={onClose}
            />

            {/* Modal */}
            <div
                className={`relative w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-[#0d0d0d] border border-border rounded-lg shadow-2xl ${show ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 translate-y-4"}`}
                style={{ transitionProperty: "opacity, transform", transitionDuration: "250ms", transitionTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)" }}
            >
                <div className="sticky top-0 z-10 flex items-start justify-between gap-4 px-5 sm:px-7 py-4 sm:py-5 border-b border-border bg-[#0d0d0d]">
                    <div>
                        <h2 className="font-display text-lg sm:text-xl font-bold tracking-tight">
                            Recarregue seus tokens
                        </h2>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                            Escolha o plano ideal e fale conosco para ativar.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="close-plans-dialog"
                        className="p-2 -mr-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors shrink-0"
                        aria-label="Fechar"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {PLANS.map((plan) => (
                        <PlanCard key={plan.id} plan={plan} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function PlanCard({ plan }) {
    const isHighlight = plan.highlight;
    const isCurrent = plan.id === "free";
    return (
        <div
            className={`relative flex flex-col border rounded-md p-4 sm:p-5 transition-colors ${
                isHighlight
                    ? "border-primary bg-primary/[0.06]"
                    : "border-border bg-[#0a0a0a] hover:border-border/80"
            }`}
            data-testid={`plan-card-${plan.id}`}
        >
            {isHighlight && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-primary text-white text-[9px] font-mono uppercase tracking-[0.2em] px-2.5 py-0.5 rounded-sm flex items-center gap-1">
                    <Zap className="w-3 h-3" strokeWidth={2.5} />
                    Popular
                </div>
            )}
            {isCurrent && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-muted border border-border text-foreground text-[9px] font-mono uppercase tracking-[0.2em] px-2.5 py-0.5 rounded-sm flex items-center gap-1">
                    <Check className="w-3 h-3" strokeWidth={2.5} />
                    Plano atual
                </div>
            )}

            <div className="font-display text-xl font-bold tracking-tight text-foreground">
                {plan.name}
            </div>

            <div className="mt-3 flex items-baseline gap-1.5">
                <span className={`font-display text-2xl sm:text-3xl font-black tracking-tighter ${isHighlight ? "text-primary" : "text-foreground"}`}>
                    {plan.price}
                </span>
                <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
                    {plan.priceNote}
                </span>
            </div>

            <ul className="mt-4 space-y-2 text-sm flex-1">
                <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary shrink-0" strokeWidth={2.5} />
                    <span>
                        <span className="font-semibold text-foreground">{plan.tokens}</span>
                        <span className="text-muted-foreground"> tokens/mês</span>
                    </span>
                </li>
                <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-primary shrink-0" strokeWidth={2.5} />
                    <span>
                        <span className="font-semibold text-foreground">{plan.tasks}</span>
                        <span className="text-muted-foreground"> tasks/mês</span>
                    </span>
                </li>
            </ul>

            {isCurrent ? (
                <div
                    data-testid={`plan-${plan.id}-current`}
                    className="mt-5 py-2.5 rounded-sm text-sm font-semibold flex items-center justify-center gap-2 border border-border bg-muted/30 text-muted-foreground cursor-default"
                >
                    <Check className="w-4 h-4" strokeWidth={2.5} />
                    Plano ativo
                </div>
            ) : (
                <a
                    href={buildWhatsappUrl(plan)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`plan-${plan.id}-contact-btn`}
                    className={`mt-5 py-2.5 rounded-sm text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                        isHighlight
                            ? "bg-primary text-white hover:opacity-90"
                            : "border border-border text-foreground hover:border-primary/50 hover:bg-primary/10"
                    }`}
                >
                    <WhatsAppIcon className="w-4 h-4" />
                    Contratar
                </a>
            )}
        </div>
    );
}
